import { Component, Input, OnInit, ViewChild } from '@angular/core';
import { ReportRecipient } from '@app/interfaces/lm/report-schedules';
import {
    StatisticsData,
    SummaryData,
    TaskTestCase,
    TaskTestCaseParams,
    TestCaseReport,
} from '@app/interfaces/lm/task-test-case';
import { STChange, STColumn } from '@app/interfaces/sys/st';
import { SendEmailComponent } from '@app/shared/components/send-email/send-email.component';
import { TaskTestCaseService } from '@services/lm/task-test-case.service';
import { SettingsService } from '@services/sys/settings.service';

@Component({
    selector: 'app-view-test-case-progress',
    templateUrl: './view-test-case-progress.component.html',
    styleUrls: ['./view-test-case-progress.component.less'],
})
export class ViewTestCaseProgressComponent implements OnInit {
    @Input() currentUser!: ReportRecipient;
    @ViewChild('sendEmail') sendEmail!: SendEmailComponent;
    isVisible = false;
    data: SummaryData[] = [];
    statisticsData: StatisticsData = {} as StatisticsData;
    loading = false;
    id!: string;
    isProject = true;
    hasClickDetail = false;
    hasClickReport = false;
    nzScroll = {
        x: '900px'
    };
    page = 0;
    size = 10;
    total = 0;
    columns: STColumn[] = [
        {
            title: 'Test Case No',
            index: 'no',
            width: '120px',
        },
        {
            title: 'Category',
            index: 'category',
            width: '120px',
        },
        {
            title: 'Sub Category',
            index: 'subCategory',
            width: '120px',
        },
        {
            title: 'Name',
            index: 'name',
            width: '250px',
        },
        {
            title: 'SME',
            index: 'sme',
            width: '60px',
        },
        {
            title: 'Expected Result',
            index: 'expectedResult',
            width: '280px',
        },
        {
            title: 'Status',
            index: 'status',
            width: '100px',
            render: 'status',
        },
        {
            title: 'Severity',
            index: 'severity',
            width: '120px',
            render: 'severity',
            iif: (item) => {
                return this.detailStatus === 'FAILED';
            },
        },
        {
            title: 'Last Update',
            index: 'lastModifiedDate',
            type: 'date',
            dateFormat: 'YYYY-MM-dd HH:mm:ss',
            width: '160px',
        },
    ];
    reportColumns: STColumn[] = [
        {
            title: 'Category',
            index: 'category',
            width: '100px',
        },
        {
            title: 'Total',
            index: 'total',
            width: '50px',
        },
        {
            title: 'Executed',
            index: 'executed',
            width: '50px',
        },
        {
            title: 'Executed Ratio',
            index: 'executedRatio',
            width: '100px',
            render: 'executedRatio',
            className: 'remove-padding',
        },
        {
            title: 'Passed',
            index: 'passed',
            width: '50px',
        },
        {
            title: 'Passed Ratio',
            index: 'passedRatio',
            width: '100px',
            render: 'passedRatio',
            className: 'remove-padding',
        },
    ];
    reportData: TestCaseReport[] = [];
    detailData: TaskTestCase[] = [];
    detailStatus: string | undefined;
    tableLoading = false;

    constructor(private taskTestCaseService: TaskTestCaseService,
                private settingsService: SettingsService) {
    }

    ngOnInit(): void {
    }

    async open(id: string, isProject: boolean = true): Promise<void> {
        this.reset();
        this.isVisible = true;
        this.id = id;
        this.isProject = isProject;
        this.data = await this.taskTestCaseService.getProgressChartOptions();
        this.statisticsData = {
            chartData: this.data,
            tableData: [
                ...this.data,
                {
                    name: 'Total',
                    value: 0,
                },
            ],
            total: 0,
        };
        await this.fetchData();
    }

    export(): void {
        if (!this.id) {
            return;
        }
        if (this.hasClickDetail) {
            this.taskTestCaseService.exportTestCase(this.isProject ? {
                projectId: this.id,
                status: this.detailStatus,
            } : {
                projectGroupId: this.id,
                status: this.detailStatus,
            });
        } else {
            this.isProject ? this.taskTestCaseService.exportProject(this.id, this.detailStatus) : this.taskTestCaseService.exportProjectGroup(this.id, this.detailStatus);
        }
    }

    async report(): Promise<void> {
        try {
            this.hasClickReport = true;
            this.tableLoading = true;
            this.reportData = await this.taskTestCaseService.getTestCaseReport(this.isProject ? {
                projectId: this.id,
            } : {
                projectGroupId: this.id,
            });
        } finally {
            this.tableLoading = false;
        }
    }

    async fetchData(): Promise<void> {
        try {
            let total = 0;
            if (!this.id) {
                return;
            }
            this.loading = true;
            const params = this.isProject ? { projectId: this.id } : { projectGroupId: this.id };
            const res = await this.taskTestCaseService.statistics(params);
            const data = this.data.map(item => {
                total += res[item.key] || 0;
                return {
                    ...item,
                    value: res[item.key] || 0,
                };
            });
            this.statisticsData = {
                chartData: data,
                tableData: [
                    ...data,
                    {
                        name: 'Total',
                        value: total,
                    },
                ],
                total,
            };
        } finally {
            this.loading = false;
        }
    }

    close(): void {
        this.isVisible = false;
    }

    reset(): void {
        this.page = 0;
        this.size = 10;
        this.detailStatus = undefined;
        this.hasClickDetail = false;
        this.hasClickReport = false;
        this.detailData = [];
        this.total = 0;
    }

    changeData(data: STChange): void {
        if (data.type === 'pi') {
            this.page = data.pi - 1;
            this.getDetailData();
        }

        if (data.type === 'ps') {
            this.size = data.ps;
            this.getDetailData();
        }
    }

    detail(status: string): void {
        this.hasClickDetail = true;
        this.detailStatus = status;
        this.getDetailData();
    }

    async getDetailData(): Promise<void> {
        try {
            const commonParams: TaskTestCaseParams = {
                page: this.page,
                size: this.size,
                status: this.detailStatus,
            };
            if (!this.detailStatus) {
                delete commonParams.status;
            }
            this.tableLoading = true;
            const { content, totalElements } = await this.taskTestCaseService.find(this.isProject ? {
                ...commonParams,
                projectId: this.id,
            } : {
                ...commonParams,
                projectGroupId: this.id,
            });
            this.detailData = content;
            this.total = totalElements;
        } finally {
            this.tableLoading = false;
        }
    }

    back(): void {
        this.reset();
    }

    formatPercent(value: string): number {
        return parseFloat(value);
    }

    getProgressColor(value: string): string {
        return this.settingsService.getGaugeColor(parseFloat(value));
    }

    send(): void {
        this.sendEmail.open(this.id);
    }
}


